import { observer } from 'mobx-react';
import { flowResult } from 'mobx';
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import momentTz from 'moment-timezone';
import Papa from 'papaparse';
import classNames from 'classnames';
import ReactPaging from '../../components/Table/ReactPaging';
import CONFIG from '../../core/configs/config';
import { MSG } from '../../core/configs/constants';
import { getSavedInfo } from '../../core/utils/browser';
import { useStore } from '../../core/utils/hook';

const ERR_EXT = 'システムはCSV形式のファイルのみインポートに対応しています。';
const ERR_TEMPLATE = 'テンプレートに不足している情報がある、または使用できません。';
const ERR_NO_DATA = 'アップロードされたファイルにデータがありません。ご確認ください。';
const EXPECTED_HEADER = ['メールアドレス', '部署コード'];
const PREVIEW_PAGE_SIZE = 10;
const IMPORT_MAX_RECORDS = 1000;
const DEPARTMENT_CODE_UNKNOWN_MSG = '部署コードが存在しません。ご確認ください。';
const ERR_IMPORT_MAX_RECORDS = '1回のインポートで登録できる件数は最大1,000件までです。再度お試しください。';

function DepartmentTransferPaging({ rowCount, page, pageSize, pageCount, onPageChange }) {
    if (rowCount <= 0) return null;
    const start = page * pageSize + 1;
    const end = Math.min((page + 1) * pageSize, rowCount);
    return (
        <div className='pagenavi department-transfer-pagenavi'>
            <div className='row'>
                <div className='col-md-6 col-sm-6 col-12'>
                    <div className='prev'>
                        <span>
                            {rowCount} 件中 {start}～{end} 件を表示
                        </span>
                    </div>
                </div>
                <div className='col-md-6 col-sm-6 col-12'>
                    <div className='next my-paginagion'>
                        <ReactPaging pageCount={pageCount} forcePage={page} onPageChange={onPageChange} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function normalizeHeaderRow(row) {
    if (!row || !row.length) return [];
    return row.map((c, i) => {
        let s = String(c ?? '').trim();
        if (i === 0) s = s.replace(/^\uFEFF/, '');
        return s;
    });
}

function headerMatches(row) {
    if (row.length !== EXPECTED_HEADER.length) return false;
    return EXPECTED_HEADER.every((h, i) => row[i] === h);
}

function decodeCsvBuffer(buf) {
    let shiftText;
    try {
        shiftText = new TextDecoder('shift_jis', { fatal: false }).decode(buf);
    } catch {
        shiftText = new TextDecoder('utf-8', { fatal: false }).decode(buf);
    }
    let parsed = Papa.parse(shiftText, {
        delimiter: ',',
        skipEmptyLines: 'greedy',
        header: false
    });
    let norm = normalizeHeaderRow(parsed.data?.[0]);
    if (headerMatches(norm)) return shiftText;

    const utfText = new TextDecoder('utf-8', { fatal: false }).decode(buf);
    parsed = Papa.parse(utfText, {
        delimiter: ',',
        skipEmptyLines: 'greedy',
        header: false
    });
    norm = normalizeHeaderRow(parsed.data?.[0]);
    if (headerMatches(norm)) return utfText;

    return shiftText;
}

async function postDepartmentTransferImport(file) {
    const http = CONFIG.ssl ? 'https' : 'http';
    const path = `${http}://${CONFIG.api_url}/department-transfer/import-csv`;
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(path, formData, {
        headers: {
            Accept: 'application/json',
            Authorization: getSavedInfo('token') || getSavedInfo('tempToken'),
            Timezone: momentTz.tz.guess()
        }
    });
}

const DepartmentTransferModal = observer(({ onClose, onDownloadTemplate, onReloadStudentList }) => {
    const fileInputRef = useRef(null);
    const pendingFileRef = useRef(null);

    const [step, setStep] = useState('upload');
    const [previewRows, setPreviewRows] = useState([]);
    const [previewPage, setPreviewPage] = useState(0);
    const [resultPage, setResultPage] = useState(0);
    const [importResult, setImportResult] = useState(null);

    const {
        modalStore: { openErrorModal },
        apiStore: { showLoading, hideLoading },
        groupAdminStore: { childrenDepartments, getCompanyDepartments }
    } = useStore();

    const departmentNameByCode = useMemo(() => {
        const m = new Map();
        (childrenDepartments || []).forEach((d) => {
            if (d?.department_code != null && d.department_code !== '') {
                m.set(String(d.department_code).trim(), d.department_name);
            }
        });
        return m;
    }, [childrenDepartments]);

    const failedByLine = useMemo(() => {
        const fr = importResult?.failedRecords;
        if (!fr?.length) return new Map();
        return new Map(fr.map((r) => [r.line, r.errors || []]));
    }, [importResult]);

    const lastLineByEmailOnClient = useMemo(() => {
        const m = new Map();
        previewRows.forEach((r) => {
            const email = (r.email || '').trim().toLowerCase();
            if (email) {
                m.set(email, r.line);
            }
        });
        return m;
    }, [previewRows]);

    const pageCount = useMemo(
        () => Math.max(1, Math.ceil(previewRows.length / PREVIEW_PAGE_SIZE)),
        [previewRows.length]
    );

    const pagedPreviewRows = useMemo(
        () =>
            previewRows.slice(
                previewPage * PREVIEW_PAGE_SIZE,
                (previewPage + 1) * PREVIEW_PAGE_SIZE
            ),
        [previewRows, previewPage]
    );

    const pagedResultRows = useMemo(
        () =>
            previewRows.slice(
                resultPage * PREVIEW_PAGE_SIZE,
                (resultPage + 1) * PREVIEW_PAGE_SIZE
            ),
        [previewRows, resultPage]
    );

    const getDepartmentPreview = (departmentCode) => {
        const code = (departmentCode || '').trim();
        if (!code) return { kind: 'empty' };
        const name = departmentNameByCode.get(code);
        if (name != null) return { kind: 'named', text: name };
        return { kind: 'unknown', code };
    };

    const resolveNewDepartmentCell = (departmentCode) => {
        const p = getDepartmentPreview(departmentCode);
        if (p.kind === 'empty') return '--';
        if (p.kind === 'named') return p.text;
        return p.code;
    };

    useEffect(() => {
        window.$('#departmentTransferModal').modal({
            backdrop: 'static',
            keyboard: false
        });
    }, []);

    useEffect(() => {
        const $m = window.$('#departmentTransferModal');
        const onHidden = () => {
            setStep('upload');
            setPreviewRows([]);
            setPreviewPage(0);
            setResultPage(0);
            setImportResult(null);
            pendingFileRef.current = null;
            if (fileInputRef.current) fileInputRef.current.value = '';
        };
        $m.on('hidden.bs.modal', onHidden);
        return () => {
            $m.off('hidden.bs.modal', onHidden);
        };
    }, []);

    useEffect(() => {
        if (step !== 'preview') return;
        flowResult(getCompanyDepartments());
    }, [step]);

    const handleUploadZoneClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        if (!file.name.toLowerCase().endsWith('.csv')) {
            openErrorModal('エラー', ERR_EXT);
            return;
        }

        let text;
        try {
            const buf = await file.arrayBuffer();
            text = decodeCsvBuffer(buf);
        } catch {
            openErrorModal('エラー', ERR_TEMPLATE);
            return;
        }

        const parsed = Papa.parse(text, {
            delimiter: ',',
            skipEmptyLines: 'greedy',
            header: false
        });

        if (!parsed.data?.length) {
            openErrorModal('エラー', ERR_NO_DATA);
            return;
        }

        const headerRow = normalizeHeaderRow(parsed.data[0]);
        if (!headerMatches(headerRow)) {
            openErrorModal('エラー', ERR_TEMPLATE);
            return;
        }

        const dataRows = parsed.data.slice(1).filter((row) =>
            row.some((cell) => String(cell ?? '').trim() !== '')
        );

        if (dataRows.length === 0) {
            openErrorModal('エラー', ERR_NO_DATA);
            return;
        }
        if (dataRows.length > IMPORT_MAX_RECORDS) {
            openErrorModal('エラー', ERR_IMPORT_MAX_RECORDS);
            return;
        }

        const rows = dataRows.map((row, idx) => ({
            line: idx + 1,
            email: String(row[0] ?? '').trim(),
            departmentCode: String(row[1] ?? '').trim()
        }));

        pendingFileRef.current = file;
        setPreviewPage(0);
        setPreviewRows(rows);
        setStep('preview');
    };

    const handleImport = async () => {
        const file = pendingFileRef.current;
        if (!file) {
            openErrorModal('エラー', MSG['api.response.server_error']);
            return;
        }
        showLoading();
        try {
            const res = await postDepartmentTransferImport(file);
            hideLoading();
            if (res.data?.ok) {
                setImportResult(res.data.data);
                setResultPage(0);
                setStep('result');
            } else {
                openErrorModal('エラー', res.data?.message || MSG['api.response.server_error']);
            }
        } catch (err) {
            hideLoading();
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                MSG['api.response.server_error'];
            openErrorModal('エラー', msg);
        }
    };

    const handleCloseHeader = () => {
        if (step === 'result') onReloadStudentList?.();
        onClose();
    };

    const handleCloseResult = () => {
        onReloadStudentList?.();
        onClose();
    };

    const handleCancel = () => {
        onClose();
    };

    const tabClass = (key) =>
        classNames('department-transfer-tab', { active: step === key });

    const actionsClass = classNames('department-transfer-modal-actions', {
        'department-transfer-modal-actions--split': step === 'preview'
    });

    const renderStackCell = (label, children) => (
        <div className='department-transfer-stack-cell'>
            <div className='department-transfer-stack-label'>{label}</div>
            {children}
        </div>
    );

    const renderPreviewRow = (r, i) => {
        const deptPv = getDepartmentPreview(r.departmentCode);
        const rowClass = classNames('department-transfer-stack-row', {
            'department-transfer-stack-row--alt': i % 2 === 1,
            'department-transfer-stack-row--unknown-dept': deptPv.kind === 'unknown'
        });

        let deptContent;
        if (deptPv.kind === 'empty') {
            deptContent = <div className='department-transfer-stack-value'>--</div>;
        } else if (deptPv.kind === 'named') {
            deptContent = <div className='department-transfer-stack-value'>{deptPv.text}</div>;
        } else {
            deptContent = (
                <>
                    <div className='department-transfer-stack-value department-transfer-preview-dept-code-unknown'>
                        {deptPv.code}
                    </div>
                    <div className='department-transfer-preview-dept-msg'>{DEPARTMENT_CODE_UNKNOWN_MSG}</div>
                </>
            );
        }

        return (
            <div key={r.line} className={rowClass}>
                <div className='department-transfer-stack-cols'>
                    {renderStackCell('行', <div className='department-transfer-stack-value'>{r.line}</div>)}
                    {renderStackCell(
                        'メールアドレス',
                        <div className='department-transfer-stack-value'>{r.email}</div>
                    )}
                    {renderStackCell('異動先部署', deptContent)}
                </div>
            </div>
        );
    };

    const renderResultRow = (pr, i) => {
        const email = (pr.email || '').trim().toLowerCase();
        const isSkipped = email && lastLineByEmailOnClient.get(email) !== pr.line;

        const errs = failedByLine.get(pr.line);
        const isFail = !isSkipped && errs != null && errs.length > 0;
        const rowClass = classNames('department-transfer-stack-row', {
            'department-transfer-stack-row--alt': i % 2 === 1
        });

        return (
            <div key={pr.line} className={rowClass}>
                <div className='department-transfer-stack-cols department-transfer-stack-cols--result'>
                    {renderStackCell('行', <div className='department-transfer-stack-value'>{pr.line}</div>)}
                    {renderStackCell(
                        'メールアドレス',
                        <div className='department-transfer-stack-value'>{pr.email}</div>
                    )}
                    {renderStackCell(
                        '異動先部署',
                        <div className='department-transfer-stack-value'>
                            {resolveNewDepartmentCell(pr.departmentCode)}
                        </div>
                    )}
                    {renderStackCell(
                        'ステータス',
                        <div
                            className={classNames('department-transfer-stack-value', {
                                'department-transfer-status-fail': isFail,
                                'department-transfer-status-ok': !isFail && !isSkipped
                            })}
                        >
                            {isSkipped ? '--' : isFail ? '失敗' : '成功'}
                        </div>
                    )}
                    {renderStackCell(
                        'エラー内容',
                        <div
                            className={classNames('department-transfer-stack-value', {
                                'department-transfer-err-text': isFail
                            })}
                        >
                            {isSkipped ? '--' : isFail
                                ? errs.map((err, idx) => (
                                    <div key={`${pr.line}-err-${idx}`}>{err}</div>
                                ))
                                : '--'}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const pagingShared = {
        rowCount: previewRows.length,
        pageSize: PREVIEW_PAGE_SIZE,
        pageCount
    };

    return (
        <div className='modal' id='departmentTransferModal'>
            <div className='modal-dialog modal-lg department-transfer-modal-dialog'>
                <div className='modal-content department-transfer-modal-content'>
                    <div className='modal-header'>
                        <h3 className='title-1'>部署異動</h3>
                        <button type='button' className='btn-close' onClick={handleCloseHeader}></button>
                    </div>
                    <div className='department-transfer-tabs'>
                        <span className={tabClass('upload')}>ファイルをアップロード</span>
                        <span className={tabClass('preview')}>プレビュー</span>
                        <span className={tabClass('result')}>結果</span>
                    </div>
                    <div className='modal-body department-transfer-modal-body'>
                        {step === 'upload' && (
                            <>
                                <p className='department-transfer-instruction'>
                                    部署異動対象の受講者一覧をアップロードしてください
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type='file'
                                    accept='.csv'
                                    className='department-transfer-file-input'
                                    onChange={handleFileChange}
                                />
                                <div
                                    className='department-transfer-upload-zone'
                                    role='button'
                                    tabIndex={0}
                                    onClick={handleUploadZoneClick}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleUploadZoneClick();
                                        }
                                    }}
                                >
                                    <div className='department-transfer-upload-inner'>
                                        <i className='fa-solid fa-cloud-arrow-up department-transfer-upload-icon' aria-hidden></i>
                                        <div>
                                            <span className='department-transfer-select-link'>ファイルを選択する</span>
                                        </div>
                                        <p className='department-transfer-format'>対応フォーマット: .CSV</p>
                                    </div>
                                </div>
                                <div className='department-transfer-template-row'>
                                    <a role='button' onClick={onDownloadTemplate}>
                                        CSVテンプレートをダウンロード
                                    </a>
                                </div>
                            </>
                        )}

                        {step === 'preview' && (
                            <>
                                <p className='department-transfer-preview-warning'>
                                    受講者一覧および異動先部署の情報をご確認ください
                                </p>
                                <div className='department-transfer-stack-list'>
                                    {pagedPreviewRows.map((r, i) => renderPreviewRow(r, i))}
                                </div>
                                <DepartmentTransferPaging
                                    {...pagingShared}
                                    page={previewPage}
                                    onPageChange={({ selected }) => setPreviewPage(selected)}
                                />
                            </>
                        )}

                        {step === 'result' && importResult && (
                            <>
                                <div className='department-transfer-result-summary'>
                                    <span>
                                        インポート成功 :{' '}
                                        <span className='department-transfer-result-num-ok'>
                                            {importResult.successfulRecords}
                                        </span>
                                        件
                                    </span>
                                    <span className='department-transfer-result-summary-gap'>
                                        インポート失敗 :{' '}
                                        <span className='department-transfer-result-num-ng'>
                                            {importResult.failedRecords?.length ?? 0}
                                        </span>
                                        件
                                    </span>
                                </div>
                                <div className='department-transfer-stack-list'>
                                    {pagedResultRows.map((pr, i) => renderResultRow(pr, i))}
                                </div>
                                <DepartmentTransferPaging
                                    {...pagingShared}
                                    page={resultPage}
                                    onPageChange={({ selected }) => setResultPage(selected)}
                                />
                            </>
                        )}
                    </div>
                    <div className={actionsClass}>
                        {step === 'preview' ? (
                            <>
                                <button type='button' className='btn-cancel' onClick={handleCancel}>
                                    キャンセル
                                </button>
                                <button
                                    type='button'
                                    className={classNames('btn-submit', 'department-transfer-btn-import')}
                                    onClick={handleImport}
                                >
                                    インポート
                                </button>
                            </>
                        ) : step === 'result' ? (
                            <button type='button' className='btn-cancel' onClick={handleCloseResult}>
                                閉じる
                            </button>
                        ) : (
                            <button type='button' className='btn-cancel' onClick={handleCancel}>
                                キャンセル
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default DepartmentTransferModal;
